/**
 * Trade-alert preferences (vibration + sound on BUY/SELL fills).
 *
 * Stored per-device in localStorage. Separate toggles for buy vs sell, and for
 * vibration vs sound, as requested. Vibration only fires on devices that
 * support the Vibration API (mobile); on desktop it's a silent no-op.
 */

const KEY = "griddesk.tradeAlerts.v1";

export const DEFAULT_ALERT_PREFS = {
    soundBuy: false,
    soundSell: false,
    vibrateBuy: false,
    vibrateSell: false,
    volume: 0.5, // 0..1
};

export function loadAlertPrefs() {
    try {
        const raw = localStorage.getItem(KEY);
        if (!raw) return { ...DEFAULT_ALERT_PREFS };
        const parsed = JSON.parse(raw);
        return { ...DEFAULT_ALERT_PREFS, ...parsed };
    } catch (_) {
        return { ...DEFAULT_ALERT_PREFS };
    }
}

export function saveAlertPrefs(prefs) {
    try {
        localStorage.setItem(KEY, JSON.stringify({ ...DEFAULT_ALERT_PREFS, ...prefs }));
    } catch (_) { /* storage unavailable — ignore */ }
}

/** True if the current device exposes the Vibration API (typically mobile). */
export function vibrationSupported() {
    return typeof navigator !== "undefined" && typeof navigator.vibrate === "function";
}

// ---- Sound synthesis (WebAudio, no asset files) --------------------------
// A single shared AudioContext, lazily created on first use. Browsers require
// a user gesture before audio can start; the settings "Test" buttons and the
// natural click-to-open-settings interaction satisfy that.

let _audioCtx = null;
function audioCtx() {
    if (typeof window === "undefined") return null;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    if (!_audioCtx) _audioCtx = new AC();
    if (_audioCtx.state === "suspended") {
        _audioCtx.resume().catch(() => {});
    }
    return _audioCtx;
}

/**
 * Play a short two-note chime. BUY = ascending (brighter), SELL = descending.
 * Distinct so the two are audibly different, as requested.
 */
export function playAlertSound(side, volume = 0.5) {
    const ctx = audioCtx();
    if (!ctx) return;
    const now = ctx.currentTime;
    // BUY rises (e.g. 660→990 Hz); SELL falls (e.g. 620→415 Hz).
    const freqs = side === "BUY" ? [660, 990] : [620, 415];
    const noteDur = 0.12;
    const vol = Math.max(0, Math.min(1, volume)) * 0.35;

    freqs.forEach((f, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = side === "BUY" ? "triangle" : "sine";
        osc.frequency.setValueAtTime(f, now + i * noteDur);
        const start = now + i * noteDur;
        const end = start + noteDur;
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(vol, start + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, end);
        osc.connect(gain).connect(ctx.destination);
        osc.start(start);
        osc.stop(end + 0.02);
    });
}

/** Vibrate with a side-specific pattern (no-op where unsupported). */
export function vibrate(side) {
    if (!vibrationSupported()) return;
    // BUY = single short buzz; SELL = double buzz. Distinguishable by feel.
    const pattern = side === "BUY" ? [60] : [50, 40, 50];
    try { navigator.vibrate(pattern); } catch (_) { /* ignore */ }
}

/** Fire whatever alerts the prefs enable for a given side. */
export function fireAlerts(side, prefs) {
    if (!prefs) return;
    const isBuy = side === "BUY";
    if ((isBuy && prefs.soundBuy) || (!isBuy && prefs.soundSell)) {
        playAlertSound(side, prefs.volume);
    }
    if ((isBuy && prefs.vibrateBuy) || (!isBuy && prefs.vibrateSell)) {
        vibrate(side);
    }
}
